// Self-audit for one billing run — what a program-integrity reviewer would
// stop on. The rules and the reasons for them are in lib/billingAudit.js; this
// only loads what they need and lays the findings out.
//
// Advisory. It never blocks an export and never changes a line: some findings
// are legitimate and only a person can say which.
import { useEffect, useState } from 'react';
import { auditRun, AUDIT_ORDER } from '../../../lib/billingAudit';
import { loadAuditBase, loadAuditVisits } from './auditInputs';

const TONE = {
  serious: { fg: 'var(--danger)', bg: 'var(--danger-bg)', border: 'var(--danger-border)', label: 'Serious' },
  review:  { fg: 'var(--warn)',   bg: 'var(--warn-bg)',   border: 'var(--warn-border)',   label: 'Review' },
};

export default function RunAuditPanel({ run, lines }) {
  const [base, setBase]       = useState(null);   // clients, auth history, other runs' lines
  const [visits, setVisits]   = useState(null);   // null = loading/not loaded
  const [staff, setStaff]     = useState([]);
  const [visitErr, setVisitErr] = useState('');
  const [open, setOpen]       = useState({});

  const clientIds = [...new Set((lines || []).map((l) => l.client_id).filter(Boolean))];
  const idsKey = clientIds.sort().join(',');

  useEffect(() => {
    let live = true;
    setBase(null);
    if (!run?.date_from || !lines?.length) return undefined;
    loadAuditBase(run, lines).then((b) => { if (live) setBase(b); });
    return () => { live = false; };
  }, [run?.id, run?.date_from, run?.date_to, idsKey, lines?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let live = true;
    setVisits(null); setVisitErr('');
    if (!run?.date_from) return undefined;
    loadAuditVisits(run).then((r) => {
      if (!live) return;
      if (r.error) { setVisitErr(r.error); setVisits(undefined); return; }
      setVisits(r.visits);
      setStaff(r.staff);
    });
    return () => { live = false; };
  }, [run?.id, run?.date_from, run?.date_to]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lines?.length) return null;

  const shell = (children, tone = null) => (
    <div style={{ margin: '12px 0', border: `0.5px solid ${tone ? tone.border : 'var(--border)'}`, borderRadius: 8, overflow: 'hidden', background: 'var(--bg)' }}>
      {children}
    </div>
  );
  const heading = (text, sub, tone) => (
    <div style={{ padding: '8px 12px', background: tone ? tone.bg : 'var(--bg2)', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: tone ? tone.fg : 'var(--text2)' }}>{text}</span>
      {sub && <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{sub}</span>}
    </div>
  );

  if (!base) return shell(heading('Self-audit', 'Checking this run…'));
  if (base.error) return shell(heading('Self-audit', `Could not load what the checks need: ${base.error}`));

  const result = auditRun({
    run, lines, ...base,
    visits: Array.isArray(visits) ? visits : null,
    staff,
  });
  const checks = [...result.checks].sort((a, b) => AUDIT_ORDER.indexOf(a.key) - AUDIT_ORDER.indexOf(b.key));
  const top = result.serious ? TONE.serious : result.review ? TONE.review : null;

  const summary = [
    result.serious ? `${result.serious} serious` : null,
    result.review ? `${result.review} to review` : null,
  ].filter(Boolean).join(' · ') || 'Nothing a reviewer would stop on';

  const caregiverNote = visits === null
    ? 'Checking caregivers against the calendar…'
    : visits === undefined
      ? `Caregiver checks unavailable — ${visitErr}`
      : null;

  return shell(<>
    {heading('Self-audit', summary, top)}
    <div style={{ padding: '6px 12px 0', fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.5 }}>
      What a Medicaid program-integrity review would look for in this run. Advisory — nothing here blocks the export or changes a line.
      {caregiverNote && <div style={{ marginTop: 2 }}>{caregiverNote}</div>}
    </div>
    {checks.map((c) => {
      const tone = TONE[c.severity];
      const expanded = open[c.key] ?? c.severity === 'serious';
      const shown = expanded ? c.items : [];
      return (
        <div key={c.key} style={{ padding: '10px 12px', borderTop: '0.5px solid var(--border)' }}>
          <button type="button" onClick={() => setOpen((o) => ({ ...o, [c.key]: !expanded }))}
            style={{ all: 'unset', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'baseline', width: '100%' }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: tone.fg,
                           background: tone.bg, border: `0.5px solid ${tone.border}`, borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>
              {tone.label}
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', flex: 1 }}>{c.title}</span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{c.items.length} · {expanded ? 'hide' : 'show'}</span>
          </button>
          {expanded && (
            <>
              <div style={{ fontSize: 11.5, color: 'var(--text2)', margin: '6px 0 4px', lineHeight: 1.5 }}>{c.why}</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: 'var(--text)', lineHeight: 1.6 }}>
                {shown.map((it, i) => <li key={i}>{it.text}</li>)}
              </ul>
            </>
          )}
        </div>
      );
    })}
    <div style={{ padding: '8px 12px', borderTop: '0.5px solid var(--border)', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
      Not checked: hospital stays and Medicaid eligibility dates — CAERA holds neither, and GAMMIS checks both.
      {result.unknownAuthHours.length > 0 && (
        <div style={{ marginTop: 2 }}>
          Authorised weekly hours are not on file for {result.unknownAuthHours.join('; ')}, so their hours could not be checked against the authorisation.
        </div>
      )}
    </div>
  </>, top);
}
